import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, IndianRupee, Clock, CheckCircle2, AlertCircle, FileText, ArrowRight, LogOut } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  work_initiated: { label: 'Work Initiated', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50', step: 1 },
  under_process: { label: 'Under Process', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50', step: 2 },
  pending: { label: 'Pending', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50', step: 2 },
  completed: { label: 'Completed', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50', step: 3 },
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.work_initiated;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${config.bgLight} ${config.textColor}`} data-testid={`status-badge-${status}`}>
      <span className={`w-2 h-2 rounded-full ${config.color}`}></span>
      {config.label}
    </span>
  );
};

const ProgressTracker = ({ status }) => {
  const steps = [
    { key: 'work_initiated', label: 'Work Initiated' },
    { key: 'under_process', label: 'Under Process' },
    { key: 'completed', label: 'Completed' },
  ];
  const currentStep = status === 'pending' ? 1 : (STATUS_CONFIG[status]?.step || 1);

  return (
    <div className="flex items-center gap-1 w-full mt-3" data-testid="progress-tracker">
      {steps.map((step, idx) => {
        const isActive = idx < currentStep;
        const isCurrent = idx === currentStep - 1;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center flex-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isActive ? 'bg-secondary text-white' : 'bg-muted text-muted-foreground'} ${isCurrent ? 'ring-2 ring-secondary ring-offset-2' : ''}`}>
                {isActive ? <CheckCircle2 className="h-4 w-4" /> : idx + 1}
              </div>
              <span className={`text-[10px] mt-1 text-center ${isActive ? 'text-secondary font-medium' : 'text-muted-foreground'}`}>{step.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`h-0.5 flex-1 -mt-4 ${idx < currentStep - 1 ? 'bg-secondary' : 'bg-muted'}`}></div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const ClientPortal = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) fetchDashboard();
  }, [user]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/client/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }
  if (!user) return <Navigate to="/login" />;

  const summary = dashboard?.summary || { total_services: 0, active_services: 0, total_paid: 0, total_pending: 0 };
  const requests = dashboard?.service_requests || [];

  return (
    <div className="bg-background py-24 md:py-32" data-testid="client-portal-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold font-playfair" data-testid="portal-title">Welcome, {user.name}</h1>
            <p className="text-muted-foreground mt-1">Track your services and payments</p>
          </div>
          <div className="flex gap-2">
            <Link to="/services">
              <Button variant="outline" size="sm" data-testid="browse-services-btn">
                <ArrowRight className="mr-1.5 h-4 w-4" /> Browse Services
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn">
              <LogOut className="mr-1.5 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-secondary" /></div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <Card data-testid="summary-total-services">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-blue-50"><FileText className="h-5 w-5 text-blue-600" /></div>
                    <div>
                      <p className="text-2xl font-bold">{summary.total_services}</p>
                      <p className="text-xs text-muted-foreground">Total Services</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="summary-active-services">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-amber-50"><Clock className="h-5 w-5 text-amber-600" /></div>
                    <div>
                      <p className="text-2xl font-bold">{summary.active_services}</p>
                      <p className="text-xs text-muted-foreground">In Progress</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="summary-total-paid">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-emerald-50"><IndianRupee className="h-5 w-5 text-emerald-600" /></div>
                    <div>
                      <p className="text-2xl font-bold">{summary.total_paid.toLocaleString('en-IN')}</p>
                      <p className="text-xs text-muted-foreground">Total Paid</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="summary-pending-amount">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-red-50"><AlertCircle className="h-5 w-5 text-red-500" /></div>
                    <div>
                      <p className="text-2xl font-bold">{summary.total_pending.toLocaleString('en-IN')}</p>
                      <p className="text-xs text-muted-foreground">Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Service Requests */}
            <Card data-testid="service-requests-section">
              <CardHeader>
                <CardTitle className="text-xl font-playfair">Your Service Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {requests.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground/40" />
                    <p className="text-muted-foreground mb-4">No service requests yet</p>
                    <Link to="/services">
                      <Button data-testid="explore-services-btn">Explore Our Services</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {requests.map((req, idx) => (
                      <div key={req.id || idx} className="border rounded-xl p-5 hover:shadow-sm transition-shadow" data-testid={`service-request-${idx}`}>
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-3">
                          <div>
                            <h3 className="font-semibold text-base">{req.service_type}</h3>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Requested on {new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                          </div>
                          <StatusBadge status={req.status} />
                        </div>

                        {/* Progress Tracker */}
                        <ProgressTracker status={req.status} />

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Amount</p>
                            <p className="font-semibold text-sm">{req.total_amount ? `₹${Number(req.total_amount).toLocaleString('en-IN')}` : '-'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid</p>
                            <p className="font-semibold text-sm text-emerald-600">{req.amount_paid ? `₹${Number(req.amount_paid).toLocaleString('en-IN')}` : '₹0'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Pending</p>
                            <p className="font-semibold text-sm text-red-500">{req.amount_pending ? `₹${Number(req.amount_pending).toLocaleString('en-IN')}` : '₹0'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Est. Timeline</p>
                            <p className="font-semibold text-sm">{req.estimated_days || req.estimated_completion || '-'}</p>
                          </div>
                        </div>

                        {req.admin_notes && (
                          <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                            <p className="text-xs text-muted-foreground font-medium">Note from team:</p>
                            <p className="text-sm mt-0.5">{req.admin_notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Help Section */}
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Need help? Call us at{' '}
                <a href="tel:+919406563535" className="text-secondary font-semibold hover:underline">+91 94065 63535</a>
                {' '}or{' '}
                <a href="https://wa.me/919406563535" target="_blank" rel="noopener noreferrer" className="text-secondary font-semibold hover:underline">WhatsApp</a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClientPortal;
